"use client";

import { Minus, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MarkThePartOrAttachATag() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: panelsRef,
    isVisible: panelsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const tiers = [{
    number: "01",
    name: "STANDARD MARKING",
    descriptor: "Essential identification solutions for industrial compliance and asset tracking.",
    featured: false,
    price: "From $150",
    features: [{
      included: true,
      text: "Standard phenolic tags"
    }, {
      included: true,
      text: "Basic laser etching"
    }, {
      included: true,
      text: "Single-color engraving"
    }, {
      included: true,
      text: "Standard adhesive backing"
    }, {
      included: false,
      text: "Custom logo integration"
    }, {
      included: false,
      text: "Arc flash compliance analysis"
    }, {
      included: false,
      text: "Direct part marking (DPM)"
    }]
  }, {
    number: "02",
    name: "COMPLIANCE SUITE",
    descriptor: "Comprehensive labeling systems engineered for safety and regulatory standards.",
    featured: true,
    price: "From $450",
    features: [{
      included: true,
      text: "Arc flash warning labels"
    }, {
      included: true,
      text: "Multi-layer phenolic tags"
    }, {
      included: true,
      text: "Custom color-coded systems"
    }, {
      included: true,
      text: "Precision laser engraving"
    }, {
      included: true,
      text: "Submittal package documentation"
    }, {
      included: true,
      text: "Durable outdoor-rated materials"
    }, {
      included: false,
      text: "High-volume DPM serialization"
    }]
  }, {
    number: "03",
    name: "PRECISION DPM",
    descriptor: "Advanced direct part marking and permanent identification for critical components.",
    featured: false,
    price: "From $950",
    features: [{
      included: true,
      text: "Direct part marking (DPM)"
    }, {
      included: true,
      text: "Deep-engraved metal serialization"
    }, {
      included: true,
      text: "Automated data matrix generation"
    }, {
      included: true,
      text: "Aerospace-grade material marking"
    }, {
      included: true,
      text: "Full traceability reporting"
    }, {
      included: true,
      text: "Custom fixture engineering"
    }, {
      included: true,
      text: "High-volume batch processing"
    }, {
      included: true,
      text: "Dedicated account management"
    }]
  }];
  return <section data-section-id="3547" id="mark-the-part-or-attach-a-tag" className="relative bg-background text-foreground overflow-hidden py-24 md:py-32 lg:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[600px] w-[600px] rounded-lg" style={{
      background: "radial-gradient(circle, hsla(45, 68%, 47%, 0.08) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs tracking-widest text-muted-foreground mb-6 md:mb-8">
              [ 02 / SOLUTIONS ]
            </p>
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight uppercase">
              Industrial
              <br />
              <span className="text-primary">Precision.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground">
              Professional marking and engraving services tailored to your specific industrial requirements and safety standards.
            </p>
          </div>
        </div>

        {}
        <div ref={panelsRef} className={`mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 items-stretch transition-all duration-700 ease-out delay-150 ${panelsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {tiers.map((tier, i) => <div data-index={i} key={tier.number} className={`group relative flex flex-col backdrop-blur-xl bg-card shadow-3xl transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-4xl ${tier.featured ? "border border-primary lg:-mt-16 lg:mb-0" : "border border-border"}`} style={{
          background: "linear-gradient(180deg, hsla(0, 0%, 100%, 0.03) 0%, hsla(0, 0%, 100%, 0.01) 100%)"
        }}>
              {tier.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="bg-primary px-4 py-1.5">
                    <span className="font-mono text-[10px] tracking-widest text-primary-foreground uppercase whitespace-nowrap">
                      [ Recommended ]
                    </span>
                  </div>
                </div>}

              <div className="flex flex-col flex-1 p-8 md:p-10">
                {}
                <div className={`font-default font-bold text-7xl md:text-8xl leading-none tracking-tight ${tier.featured ? "text-primary" : "text-foreground"}`}>
                  {tier.number}
                </div>

                {}
                <h3 className="font-default font-bold text-2xl md:text-3xl tracking-tight uppercase mt-8">
                  {tier.name}
                </h3>

                {}
                <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-3">
                  {tier.descriptor}
                </p>

                {}
                <ul className="mt-10 space-y-4 flex-1">
                  {tier.features.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3">
                      {feature.included ? <Plus className="size-4 mt-1 shrink-0 text-primary" strokeWidth={1.5} /> : <Minus className="size-4 mt-1 shrink-0 text-muted-foreground/50" strokeWidth={1.5} />}
                      <span className={`font-default text-base leading-snug ${feature.included ? "text-card-foreground" : "text-muted-foreground/60 line-through decoration-muted-foreground/30"}`}>
                        {feature.text}
                      </span>
                    </li>)}
                </ul>

                {}
                <div className="mt-10 h-px w-full bg-border" />

                {}
                {i !== 0 && i !== 1}

                {}
                {i !== 0 && i !== 1 && <div className="mt-8">
                    {tier.featured ? <Button asChild className="w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest uppercase shadow-lg">
                        <Link to="/ContactPage">Get Quote</Link>
                      </Button> : <Button asChild variant="outline" className="w-full h-12 rounded-lg border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground font-mono text-xs tracking-widest uppercase">
                        
                      </Button>}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}